import { Link, router, usePage } from "@inertiajs/react";
import { useEffect, useState } from "react";
import AccountQuotaChip from "@/components/domains/AccountQuotaChip";
import CoverageAuditQuotaDialog from "@/components/domains/CoverageAuditQuotaDialog";
import CoverageSection from "@/components/domains/CoverageSection";
import DomainClaimHeader from "@/components/domains/DomainClaimHeader";
import DomainCoverageAccordion from "@/components/domains/DomainCoverageAccordion";
import DomainDiscoverEducationDialog from "@/components/domains/DomainDiscoverEducationDialog";
import DomainDiscoverProgressBanner from "@/components/domains/DomainDiscoverProgressBanner";
import DomainEvaluationCompleteDialog from "@/components/domains/DomainEvaluationCompleteDialog";
import DomainFindingsPair from "@/components/domains/DomainFindingsPair";
import EvaluatePagesDialog from "@/components/domains/EvaluatePagesDialog";
import LiveEvaluationCard from "@/components/domains/LiveEvaluationCard";
import LoadingCoverageSection from "@/components/domains/LoadingCoverageSection";
import LoadingOpenIssuesList from "@/components/domains/LoadingOpenIssuesList";
import LoadingStatusGlance from "@/components/domains/LoadingStatusGlance";
import ManageMonitoredPagesDialog from "@/components/domains/ManageMonitoredPagesDialog";
import MonitoredPagesStrip from "@/components/domains/MonitoredPagesStrip";
import StatusGlance from "@/components/domains/StatusGlance";
import Layout from "@/components/Layout";
import { useLiveReload } from "@/hooks/useLiveReload";
import type {
  CoverageChecklist,
  CoverageChecklistItem,
  DiscoverablePage,
  DomainClaim,
  DomainDashboard,
  DomainEntitlements,
  DomainIssue,
  DomainJourneys,
  DomainPage,
  DomainTestUser,
  DomainTopOpenIssue,
  IndexPagination,
  JourneyRow,
  LiveEvaluation,
  MonitoredJourneySummary,
  MonitoredPageSummary,
  SharedProps,
} from "@/types";
import { DOMAIN_SHOW_LIVE_PROPS } from "@/utils/domainShowReload";
import { domainShowHref } from "@/utils/domainUrls";
import { dismissDiscoverEducation, isDiscoverEducationDismissed } from "@/utils/liveEvaluationSummary";
import { scrollMainToId } from "@/utils/scrollMain";

type ShowProps = {
  domain: DomainDashboard;
  entitlements: DomainEntitlements;
  monitored_pages: MonitoredPageSummary[];
  monitored_journeys: MonitoredJourneySummary[];
  discoverable_pages: DiscoverablePage[];
  issues: DomainIssue[];
  issues_pagination: IndexPagination;
  top_open_issue: DomainTopOpenIssue | null;
  pages: DomainPage[];
  pages_pagination: IndexPagination;
  live_evaluation: LiveEvaluation | null;
  coverage_checklist: CoverageChecklist;
  first_domain: boolean;
  guest?: boolean;
  staff?: boolean;
  claim?: DomainClaim;
  test_user?: DomainTestUser;
  journeys?: DomainJourneys;
  pages_expanded?: boolean;
};

const FIRST_PASS_WARMING_EVALUATION: LiveEvaluation = {
  phase: "warming",
  active_page_id: null,
  pages: [],
};

type PlayerFocus = {
  pageId?: string;
  interactionId?: string;
  token: number;
  stepSequences?: number[];
  screenshotId?: string;
  findingId?: string;
  viewport?: string;
};

function issuePlayerFocus(issue: DomainIssue | undefined, token: number): PlayerFocus | null {
  const focus = issue?.player_focus;
  if (!focus) {
    return null;
  }

  return {
    pageId: focus.page_id,
    interactionId: focus.interaction_id,
    token,
    screenshotId: focus.screenshot_id,
    findingId: focus.finding_id,
    viewport: focus.viewport,
  };
}

export default function Show({
  domain,
  entitlements,
  monitored_pages,
  monitored_journeys,
  discoverable_pages,
  issues,
  issues_pagination,
  top_open_issue: topOpenIssue,
  pages,
  pages_pagination,
  live_evaluation,
  coverage_checklist,
  first_domain: firstDomain,
  guest = false,
  staff = false,
  claim,
  test_user: testUser,
  journeys,
  pages_expanded: pagesExpanded = true,
}: ShowProps) {
  const { current_user } = usePage<SharedProps>().props;
  const userId = current_user?.id;
  const publicGuest = guest && !current_user;
  const exploring = !domain.mapped;
  const awaitingEvaluation = entitlements.require_evaluation_picker;
  const outOfEvalCredits = entitlements.require_evaluation_quota;
  const evaluating = domain.mapped && domain.evaluating_pages;
  const scanInProgress = exploring || evaluating || awaitingEvaluation;
  const awaitingEvaluationSummary = domain.evaluation_batch_active && !evaluating && !exploring;
  const hasIssueCounts = domain.open_issue_counts.total > 0;
  const [evaluateOpen, setEvaluateOpen] = useState(awaitingEvaluation);
  const [quotaOpen, setQuotaOpen] = useState(outOfEvalCredits);
  const [manageOpen, setManageOpen] = useState(false);
  const [startedEvaluating] = useState(evaluating);
  const showCompletedCard = !exploring && live_evaluation?.phase === "complete";
  const mappingOnly = journeys?.status === "mapping" && !evaluating;
  const firstPassInProgress =
    exploring || evaluating || (domain.evaluation_batch_active && !awaitingEvaluation && !showCompletedCard);
  const showIssueLoading = awaitingEvaluation || (firstPassInProgress && !hasIssueCounts);
  const playerEvaluation = live_evaluation ?? (firstPassInProgress ? FIRST_PASS_WARMING_EVALUATION : null);
  const batchKey = domain.evaluation_summary_batch_key;
  const [localAcknowledgedKey, setLocalAcknowledgedKey] = useState<string | null>(null);
  const [educationDismissed, setEducationDismissed] = useState<boolean | null>(null);
  const [playerFocus, setPlayerFocus] = useState<PlayerFocus | null>(() => {
    if (!publicGuest || live_evaluation?.phase !== "complete") {
      return null;
    }

    return issuePlayerFocus(
      issues.find((issue) => issue.id === topOpenIssue?.id),
      1,
    );
  });
  const [expandedIssueId, setExpandedIssueId] = useState<string | null>(null);
  const canAddPages = !guest && entitlements.can_manage_monitored_pages;
  const completeDismissed =
    !batchKey || domain.evaluation_summary_acknowledged_key === batchKey || localAcknowledgedKey === batchKey;

  const showEducationDialog =
    !guest &&
    firstDomain &&
    !awaitingEvaluation &&
    !outOfEvalCredits &&
    (exploring || evaluating) &&
    !showCompletedCard &&
    educationDismissed === false;
  const showCompleteDialog = !guest && showCompletedCard && !completeDismissed && educationDismissed !== false;

  useEffect(() => {
    if (awaitingEvaluation) {
      setEvaluateOpen(true);
    }
  }, [awaitingEvaluation]);

  useEffect(() => {
    if (outOfEvalCredits) {
      setQuotaOpen(true);
    }
  }, [outOfEvalCredits]);

  useEffect(() => {
    if (!userId) {
      setEducationDismissed(null);
      return;
    }

    setEducationDismissed(isDiscoverEducationDismissed(userId));
  }, [userId]);

  // Complete wins: if education is still open when evaluation finishes, dismiss it.
  useEffect(() => {
    if (!showCompletedCard || educationDismissed !== false || !userId) {
      return;
    }

    dismissDiscoverEducation(userId);
    setEducationDismissed(true);
  }, [showCompletedCard, educationDismissed, userId]);

  useLiveReload({
    channel: "DomainChannel",
    id: domain.id,
    only: [...DOMAIN_SHOW_LIVE_PROPS],
    enabled:
      !guest &&
      (scanInProgress ||
        awaitingEvaluationSummary ||
        journeys?.status === "mapping" ||
        (Boolean(live_evaluation) && live_evaluation?.phase !== "complete")),
  });

  const issuesHref = (page: number) =>
    domainShowHref(domain.id, {
      issuesPage: page,
      pagesPage: pages_pagination.current_page,
    });
  const pagesHref = (page: number) =>
    domainShowHref(domain.id, {
      issuesPage: issues_pagination.current_page,
      pagesPage: page,
    });

  const playerCollapsed = playerFocus
    ? false
    : mappingOnly
      ? true
      : showCompletedCard && (completeDismissed || !startedEvaluating);
  const discoverBannerPhase = discoverBannerPhaseFor({
    exploring,
    awaitingEvaluation,
    evaluating,
    userScanProgress: domain.user_scan_progress,
  });

  const acknowledgeEvaluationSummary = ({
    preserveScroll,
    onAcknowledged,
  }: {
    preserveScroll: boolean;
    onAcknowledged?: () => void;
  }) => {
    if (!batchKey) {
      return;
    }

    setLocalAcknowledgedKey(batchKey);
    router.post(
      `/domains/${domain.id}/evaluation_summary_acknowledgement`,
      {},
      {
        preserveScroll,
        preserveState: true,
        only: ["domain"],
        onSuccess: onAcknowledged,
      },
    );
  };

  const dismissComplete = () => {
    acknowledgeEvaluationSummary({ preserveScroll: true });
  };

  const dismissEducation = () => {
    if (userId) {
      dismissDiscoverEducation(userId);
    }
    setEducationDismissed(true);
  };

  const watchIssue = (issue: DomainIssue) => {
    if (!live_evaluation) {
      return;
    }

    const nextFocus = issuePlayerFocus(issue, Date.now());
    if (!nextFocus) {
      return;
    }

    setPlayerFocus(nextFocus);
  };

  const acknowledgeAndScrollToIssues = (issueId?: string) => {
    const issue = issueId ? issues.find((entry) => entry.id === issueId) : undefined;
    if (issue && live_evaluation && issue.player_focus) {
      watchIssue(issue);
      acknowledgeEvaluationSummary({ preserveScroll: true });
      return;
    }

    if (issueId) {
      setExpandedIssueId(issueId);
    }

    acknowledgeEvaluationSummary({
      preserveScroll: false,
      onAcknowledged: () => {
        const targetId = issueId ? `issue-${issueId}` : "open-issues";
        const issueOnPage = issueId ? issues.some((entry) => entry.id === issueId) : true;
        if (issueId && !issueOnPage) {
          router.get(
            domainShowHref(domain.id, {
              issuesPage: 1,
              pagesPage: pages_pagination.current_page,
            }),
            {},
            {
              preserveState: true,
              onSuccess: () => scrollMainToId(targetId),
            },
          );
          return;
        }

        scrollMainToId(targetId);
      },
    });
  };

  const watchChecklistItem = (item: CoverageChecklistItem) => {
    if (!live_evaluation) {
      return;
    }

    setPlayerFocus({
      pageId: item.page_id ?? undefined,
      interactionId: item.interaction_id,
      token: Date.now(),
      stepSequences: item.step_sequences,
      viewport: item.viewport,
    });
  };

  const watchCoveragePage = (page: DomainPage) => {
    setPlayerFocus({
      pageId: page.id,
      token: Date.now(),
    });
  };

  const watchMapping = (interactionId: string) => {
    setPlayerFocus({
      interactionId,
      token: Date.now(),
    });
  };

  const watchJourney = (journey: JourneyRow) => {
    setPlayerFocus({
      pageId: journey.id,
      interactionId: journey.interaction_id ?? undefined,
      token: Date.now(),
    });
  };

  const coverageAccordion = publicGuest ? null : journeys ? (
    <DomainCoverageAccordion
      exploring={exploring}
      domainId={domain.id}
      journeys={journeys}
      pagesExpanded={pagesExpanded}
      pages={pages}
      pagesPagination={pages_pagination}
      buildPagesHref={pagesHref}
      evaluating={evaluating}
      plan={entitlements.plan}
      auditsUsed={entitlements.audits_used}
      auditsRemaining={entitlements.audits_remaining}
      auditLimit={entitlements.audit_limit}
      auditsRefreshAt={entitlements.audits_refresh_at}
      canStartAudits={!guest}
      testUser={testUser}
      onWatchLive={watchCoveragePage}
      onWatchPage={watchCoveragePage}
      onWatchMapping={watchMapping}
      onWatchJourney={watchJourney}
    />
  ) : exploring ? (
    <LoadingCoverageSection />
  ) : (
    <CoverageSection
      domainId={domain.id}
      pages={pages}
      summary={{ evaluating }}
      paging={{ pagination: pages_pagination, buildPageHref: pagesHref }}
      plan={entitlements.plan}
      auditsUsed={entitlements.audits_used}
      auditsRemaining={entitlements.audits_remaining}
      auditLimit={entitlements.audit_limit}
      auditsRefreshAt={entitlements.audits_refresh_at}
      canStartAudits={!guest}
      onWatchLive={watchCoveragePage}
      onWatchPage={watchCoveragePage}
    />
  );
  const accordionBeforeMonitoring = Boolean(testUser && testUser.prompt === "insist");
  const playerBeforeGlance = playerEvaluation != null && (playerEvaluation.phase !== "complete" || mappingOnly);
  const liveEvaluationCard = playerEvaluation ? (
    <LiveEvaluationCard
      liveEvaluation={playerEvaluation}
      initialCollapsed={playerCollapsed}
      focusRequest={playerFocus}
      collapsible={playerEvaluation.phase === "complete" || mappingOnly}
      mappingInProgress={mappingOnly}
    />
  ) : null;
  const statusGlance = showIssueLoading ? (
    <LoadingStatusGlance phase={awaitingEvaluation ? "awaiting_track" : "evaluating"} />
  ) : (
    <StatusGlance domain={domain} evaluating={firstPassInProgress} />
  );

  return (
    <Layout
      title={domain.hostname}
      hideNav={publicGuest}
      header={guest && claim ? <DomainClaimHeader loginUrl={claim.login_url} /> : null}
    >
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div className="min-w-0">
          {publicGuest ? null : (
            <Link href="/domains" className="mb-3 inline-block text-sm text-primary hover:underline">
              Back to domains
            </Link>
          )}
          <h1 className="mb-1 text-2xl font-semibold break-words">{domain.hostname}</h1>
          <a
            href={domain.url}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-gray-500 hover:text-primary hover:underline"
          >
            {domain.url}
          </a>
        </div>
        {guest ? null : <AccountQuotaChip entitlements={entitlements} />}
      </div>

      {discoverBannerPhase ? (
        <DomainDiscoverProgressBanner phase={discoverBannerPhase} progress={domain.user_scan_progress} />
      ) : null}

      {exploring ? (
        <>
          {liveEvaluationCard}
          <LoadingStatusGlance phase="exploring" />
          <LoadingOpenIssuesList phase="exploring" />
          {coverageAccordion}
        </>
      ) : (
        <>
          {playerBeforeGlance ? liveEvaluationCard : null}
          {statusGlance}
          {playerBeforeGlance ? null : liveEvaluationCard}

          {showIssueLoading ? (
            <LoadingOpenIssuesList phase={awaitingEvaluation ? "awaiting_track" : "evaluating"} />
          ) : (
            <DomainFindingsPair
              coverage={coverage_checklist}
              canAddPages={canAddPages}
              onAddPages={() => setEvaluateOpen(true)}
              onWatchItem={watchChecklistItem}
              domainId={domain.id}
              issues={issues}
              staff={staff}
              evaluating={firstPassInProgress}
              expandedIssueId={expandedIssueId}
              paging={{
                pagination: issues_pagination,
                buildPageHref: issuesHref,
              }}
              onWatchIssue={watchIssue}
            />
          )}

          {accordionBeforeMonitoring ? coverageAccordion : null}

          {!guest && !awaitingEvaluation ? (
            <MonitoredPagesStrip
              journeys={monitored_journeys}
              pages={monitored_pages}
              canManage={entitlements.can_manage_monitored_pages}
              onManage={() => setManageOpen(true)}
            />
          ) : null}

          {accordionBeforeMonitoring ? null : coverageAccordion}
        </>
      )}

      {guest ? null : (
        <CoverageAuditQuotaDialog
          open={quotaOpen}
          variant={entitlements.plan === "free" ? "upgrade" : "topup"}
          auditLimit={entitlements.audit_limit}
          auditsRefreshAt={entitlements.audits_refresh_at}
          onDismiss={() => setQuotaOpen(false)}
        />
      )}

      {!guest && entitlements.can_manage_monitored_pages ? (
        <>
          <EvaluatePagesDialog
            open={evaluateOpen}
            onClose={() => setEvaluateOpen(false)}
            domainId={domain.id}
            pages={discoverable_pages}
            discovering={exploring}
            required={awaitingEvaluation}
          />
          <ManageMonitoredPagesDialog
            open={manageOpen}
            onClose={() => setManageOpen(false)}
            domainId={domain.id}
            pages={discoverable_pages}
            journeys={journeys?.rows}
          />
        </>
      ) : null}

      {guest ? null : (
        <DomainDiscoverEducationDialog open={showEducationDialog} onDismiss={dismissEducation} userId={userId} />
      )}

      {!guest && live_evaluation ? (
        <DomainEvaluationCompleteDialog
          open={showCompleteDialog}
          hostname={domain.hostname}
          evaluatedPagesCount={coverage_checklist.evaluated_pages_count}
          evaluatedJourneysCount={coverage_checklist.evaluated_journeys_count}
          openIssueCount={domain.open_issue_counts.total}
          topIssue={topOpenIssue}
          checklistItems={coverage_checklist.items}
          failedEvaluationNote={domain.failed_evaluation_note}
          onDismiss={dismissComplete}
          onViewIssues={acknowledgeAndScrollToIssues}
        />
      ) : null}
    </Layout>
  );
}

function discoverBannerPhaseFor({
  exploring,
  awaitingEvaluation,
  evaluating,
  userScanProgress,
}: {
  exploring: boolean;
  awaitingEvaluation: boolean;
  evaluating: boolean;
  userScanProgress: DomainDashboard["user_scan_progress"];
}): "exploring" | "interacting" | "analyzing" | null {
  if (awaitingEvaluation) {
    return null;
  }

  if (exploring) {
    return "exploring";
  }

  if (!evaluating || userScanProgress == null) {
    return null;
  }

  return userScanProgress.phase;
}
